import { useState, useEffect, useCallback, useRef } from 'react';
import { useMountedState } from 'react-use';

import { IPreviousKnm } from 'APIPackage/KnmModule/interfaces/IPreviousKnm';
import { IPreviousKnmFilter } from 'APIPackage/KnmModule/interfaces/IPreviousKnmFilter';
import { fetchKnmFindPrevious } from 'APIPackage/KnmModule/services/knmAdditionalDataApi';

const PAGE_SIZE = '10';

interface IPreviousKnmsWithState {
  knms: IPreviousKnm[];
  page: number;
  pageCount: number;
  year: number;
  loading: boolean;
  success: boolean;
}

interface IUsePreviousKnms {
  isOn?: boolean;
  year: number;
  inn?: string[];
  ogrn?: string[];
}

export function usePreviousKnms({ isOn, year, inn, ogrn }: IUsePreviousKnms) {
  const isMounted = useMountedState();
  const [yearsPreviousKnmsWithState, setYearsPreviousKnmsWithState] = useState<IPreviousKnmsWithState[]>([]);
  const activePreviousKnmsWithState = yearsPreviousKnmsWithState.find(
    yearPreviousKnmsWithState => yearPreviousKnmsWithState.year === year,
  );
  const activePreviousKnms = activePreviousKnmsWithState?.knms || [];
  const isActivePreviousKnmsLoading = activePreviousKnmsWithState?.loading;
  const isActivePreviousKnmsAvailable = Boolean(
    activePreviousKnmsWithState && (activePreviousKnmsWithState?.loading || activePreviousKnmsWithState?.success),
  );
  const isActivePreviousKnmsAvailableRef = useRef(isActivePreviousKnmsAvailable);

  isActivePreviousKnmsAvailableRef.current = isActivePreviousKnmsAvailable;

  const getPreviousKnms = useCallback(
    async (params: IPreviousKnmFilter) => {
      try {
        setYearsPreviousKnmsWithState(prev => {
          const isYearExist = prev.some(yearPreviousKnmsWithState => yearPreviousKnmsWithState.year === params.year);

          if (!isYearExist) {
            return [
              ...prev,
              {
                knms: [],
                page: 0,
                pageCount: 0,
                year: params.year,
                loading: true,
                success: false,
              },
            ];
          }

          return prev.map(yearPreviousKnmsWithState => {
            if (yearPreviousKnmsWithState.year === params.year) {
              return {
                ...yearPreviousKnmsWithState,
                knms: [],
                page: 0,
                pageCount: 0,
                loading: true,
                success: false,
              };
            }

            return yearPreviousKnmsWithState;
          });
        });
        const response = await fetchKnmFindPrevious({ data: params, size: PAGE_SIZE });

        if (isMounted()) {
          setYearsPreviousKnmsWithState(prev =>
            prev.map(yearPreviousKnmsWithState => {
              if (params.year === yearPreviousKnmsWithState.year) {
                return {
                  ...yearPreviousKnmsWithState,
                  knms: response.content,
                  page: response.pageable.pageNumber,
                  pageCount: response.totalPages,
                  success: true,
                };
              }

              return yearPreviousKnmsWithState;
            }),
          );
        }
      } finally {
        isMounted() &&
          setYearsPreviousKnmsWithState(prev =>
            prev.map(yearPreviousKnmsWithState => {
              if (params.year === yearPreviousKnmsWithState.year) {
                return {
                  ...yearPreviousKnmsWithState,
                  loading: false,
                };
              }

              return yearPreviousKnmsWithState;
            }),
          );
      }
    },
    [isMounted],
  );

  useEffect(() => {
    if (isOn && year && inn && !isActivePreviousKnmsAvailableRef.current) {
      getPreviousKnms({
        year,
        inn,
        ...(ogrn && { ogrn }),
      });
    }
  }, [isOn, year, inn, ogrn, getPreviousKnms]);

  const getActivePreviousKnmsNextPage = useCallback(async () => {
    if (!inn || !activePreviousKnmsWithState || activePreviousKnmsWithState.loading) {
      return;
    }
    const activeYear = activePreviousKnmsWithState.year;

    try {
      setYearsPreviousKnmsWithState(prev =>
        prev.map(yearPreviousKnmsWithState => {
          if (yearPreviousKnmsWithState.year === activeYear) {
            return {
              ...yearPreviousKnmsWithState,
              loading: true,
            };
          }

          return yearPreviousKnmsWithState;
        }),
      );
      const response = await fetchKnmFindPrevious({
        data: {
          year: activeYear,
          inn,
          ...(ogrn && { ogrn }),
        },
        page: `${activePreviousKnmsWithState.page + 1}`,
        size: PAGE_SIZE,
      });

      if (isMounted()) {
        setYearsPreviousKnmsWithState(prev =>
          prev.map(yearPreviousKnmsWithState => {
            if (yearPreviousKnmsWithState.year === activeYear) {
              return {
                ...yearPreviousKnmsWithState,
                knms: [...yearPreviousKnmsWithState.knms, ...response.content],
                page: response.pageable.pageNumber,
                pageCount: response.totalPages,
              };
            }

            return yearPreviousKnmsWithState;
          }),
        );
      }
    } finally {
      isMounted() &&
        setYearsPreviousKnmsWithState(prev =>
          prev.map(yearPreviousKnmsWithState => {
            if (yearPreviousKnmsWithState.year === activeYear) {
              return {
                ...yearPreviousKnmsWithState,
                loading: false,
              };
            }

            return yearPreviousKnmsWithState;
          }),
        );
    }
  }, [isMounted, activePreviousKnmsWithState, inn, ogrn]);

  return {
    getPreviousKnms,
    getActivePreviousKnmsNextPage,
    yearsPreviousKnmsWithState,
    activePreviousKnms,
    isActivePreviousKnmsLoading,
    page: activePreviousKnmsWithState?.page,
    pageCount: activePreviousKnmsWithState?.pageCount,
  };
}
